// src/components/ConfirmDialog.jsx
// `const confirm = useConfirm(); if (await confirm({ title, message, confirmLabel })) ...`
// Without a provider it falls back to window.confirm.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const ConfirmContext = createContext(null);

const browserConfirm = ({ title, message }) => Promise.resolve(window.confirm(message || title));

export function ConfirmProvider({ children }) {
    const [request, setRequest] = useState(null); // { options, resolve }
    const cancelRef = useRef(null);

    const confirm = useCallback(
        (options) => new Promise((resolve) => setRequest({ options, resolve })),
        []
    );

    const close = useCallback(
        (result) => {
            if (request) request.resolve(result);
            setRequest(null);
        },
        [request]
    );

    useEffect(() => {
        if (!request) return undefined;
        cancelRef.current?.focus(); // the safe choice has focus, so Enter never deletes by accident
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [request, close]);

    const { title, message, confirmLabel = 'Delete', danger = true } = request?.options || {};

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {request && (
                <div className="modal-backdrop" onClick={() => close(false)}>
                    <div
                        className="modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="confirm-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h2 id="confirm-title">{title}</h2>
                        {message && <p>{message}</p>}
                        <div className="modal-actions">
                            <button ref={cancelRef} className="btn" onClick={() => close(false)}>
                                Cancel
                            </button>
                            <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={() => close(true)}>
                                {confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
}

export const useConfirm = () => useContext(ConfirmContext) || browserConfirm;
